import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { Incident, IncidentCategory, IncidentStatus } from "../lib/api";
import { num, shortId } from "../lib/format";
import { formatAgoLo } from "../lib/requestStatus";
import {
  INCIDENT_CATEGORY_LABEL,
  INCIDENT_CATEGORY_ORDER,
  INCIDENT_STATUS_LABEL,
  INCIDENT_STATUS_ORDER,
  incidentStatusBadge,
  placeLabel,
} from "../lib/services";

/** 200 = เพดานหน้าเดียวของ API (maxPageLimit) — มีมากกว่านี้จะเตือนใต้ตาราง */
const PAGE_LIMIT = 200;

async function fetchIncidents(status: IncidentStatus | null) {
  const { data, error, response } = await api.GET("/v1/admin/incidents", {
    params: { query: { limit: PAGE_LIMIT, ...(status ? { status: [status] } : {}) } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

/**
 * ຄິວແຈ້ງເຫດ (ADR-0027) — ทุก role คอนโซลเห็นเรื่องในเขตตัวเอง (API กรองด้วย scope) แต่เปลี่ยนสถานะได้เฉพาะ
 * ผู้ที่ server บอก `can_update` · ตัวกรองสถานะยิงไปที่ server ส่วนจำนวนบนชิปคือ `counts` ทั้งเขต
 */
export function IncidentsPage() {
  const [status, setStatus] = useState<IncidentStatus | null>(null);
  const [category, setCategory] = useState<IncidentCategory | "">("");
  const [search, setSearch] = useState("");
  const list = useQuery({ queryKey: ["incidents", "admin", status], queryFn: () => fetchIncidents(status) });

  const counts = list.data?.counts;
  const total = counts ? INCIDENT_STATUS_ORDER.reduce((sum, s) => sum + (counts[s] ?? 0), 0) : undefined;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (list.data?.items ?? []).filter((i: Incident) => {
      if (category && i.category !== category) return false;
      if (!q) return true;
      return [i.description, i.village_name_lo, i.district_name_lo, i.reporter?.full_name ?? "", shortId(i.id)]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [list.data, category, search]);

  return (
    <div>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">ແຈ້ງເຫດ</h1>
          <p className="page-header__subtitle">ເລື່ອງທີ່ປະຊາຊົນແຈ້ງພ້ອມຮູບ ແລະ ພິກັດ ເຖິງຕາແສງທີ່ຮັບຜິດຊອບ</p>
        </div>
      </header>

      <div className="group-chips" role="tablist" aria-label="ຕົວກອງສະຖານະ">
        <button
          type="button"
          role="tab"
          aria-selected={status === null}
          className={status === null ? "group-chip is-active" : "group-chip"}
          onClick={() => setStatus(null)}
        >
          ທັງໝົດ <span className="num">{total === undefined ? "—" : num(total)}</span>
        </button>
        {INCIDENT_STATUS_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={status === s}
            className={status === s ? "group-chip is-active" : "group-chip"}
            onClick={() => setStatus(s)}
          >
            {INCIDENT_STATUS_LABEL[s]} <span className="num">{counts ? num(counts[s] ?? 0) : "—"}</span>
          </button>
        ))}
      </div>

      <div className="filter-bar" style={{ marginBottom: "var(--space-4)" }}>
        <input
          type="search"
          className="filter-search"
          style={{ maxWidth: "24rem" }}
          placeholder="ຄົ້ນຫາ ລາຍລະອຽດ, ບ້ານ, ຜູ້ແຈ້ງ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="ຄົ້ນຫາເລື່ອງແຈ້ງ"
        />
        <select
          className="filter-select"
          value={category}
          onChange={(e) => setCategory(e.target.value as IncidentCategory | "")}
          aria-label="ຕົວກອງປະເພດເລື່ອງ"
        >
          <option value="">ທຸກປະເພດ</option>
          {INCIDENT_CATEGORY_ORDER.map((c) => (
            <option key={c} value={c}>
              {INCIDENT_CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
      </div>

      <section className="table-scroll">
        {list.isPending && <div className="data-table__empty body-sm">ກຳລັງໂຫຼດ…</div>}
        {list.isError && (
          <div className="data-table__empty body-sm" style={{ color: "var(--red-600)" }}>
            ໂຫຼດບໍ່ສຳເລັດ: {list.error.message}{" "}
            <button type="button" className="btn-outline" onClick={() => void list.refetch()}>
              ລອງໃໝ່
            </button>
          </div>
        )}
        {list.data && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ແຈ້ງເມື່ອ</th>
                <th>ປະເພດ</th>
                <th>ລາຍລະອຽດ</th>
                <th>ບ້ານ</th>
                <th>ສະຖານະ</th>
                <th className="is-right">ຮູບ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="data-table__empty">
                    {list.data.items.length === 0 ? "ຍັງບໍ່ມີເລື່ອງແຈ້ງໃນເຂດນີ້" : "ບໍ່ມີເລື່ອງທີ່ກົງກັບຕົວກອງນີ້"}
                  </td>
                </tr>
              )}
              {filtered.map((i) => (
                <tr key={i.id}>
                  <td>
                    <div className="caption">{formatAgoLo(i.created_at)}</div>
                    <div className="caption num">#{shortId(i.id)}</div>
                  </td>
                  <td>
                    <span className={i.category === "emergency" ? "status-badge status-badge--danger" : "status-badge"}>
                      {INCIDENT_CATEGORY_LABEL[i.category]}
                    </span>
                  </td>
                  <td style={{ maxWidth: "22rem" }}>
                    <div className="body-sm incident-desc-clamp">{i.description}</div>
                  </td>
                  <td className="body-sm">{placeLabel(i)}</td>
                  <td>
                    <span className={incidentStatusBadge(i.status)}>{INCIDENT_STATUS_LABEL[i.status]}</span>
                  </td>
                  <td className="is-right num">{i.photo_urls.length}</td>
                  <td className="is-right">
                    <Link to={`/incidents/${i.id}`} className="btn-outline">
                      ເບິ່ງ
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {list.data?.has_more && (
        <p className="caption" style={{ marginTop: "var(--space-2)" }}>
          ສະແດງ {PAGE_LIMIT} ລາຍການລ້າສຸດ — ອາດຍັງບໍ່ຄົບ
        </p>
      )}
    </div>
  );
}
